import type { Recipe, RecipeLink, ScalePreviewResponse } from '@recipes/contracts';
import { cn } from '@recipes/ui';
import { useTranslation } from 'react-i18next';
import { IngredientLine } from '@/components/recipes/ingredient-line';
import { LinkedRecipeChip } from './base-recipes/linked-recipe';

export interface ScaledIngredientsProps {
  recipe: Pick<Recipe, 'ingredientSections'>;
  /** When present (servings differ from the original) these values are rendered instead. */
  preview?: ScalePreviewResponse;
  loading?: boolean;
  checkable?: boolean;
  checked?: Set<string>;
  onCheckedChange?: (ingredientId: string, checked: boolean) => void;
  /** Only render these ingredient ids (cooking mode "current step" view). */
  onlyIds?: Set<string>;
  size?: 'default' | 'lg';
  className?: string;
  /** Links to base recipes lead to their page and back to this recipe… */
  linkFrom?: string;
  /** …or open in place (cooking mode). */
  onOpenLinked?: (link: RecipeLink) => void;
}

/** Ingredient sections with scaled quantities and fraction display (spec 5.3). */
export function ScaledIngredients({
  recipe,
  preview,
  loading,
  checkable,
  checked,
  onCheckedChange,
  onlyIds,
  size = 'default',
  className,
  linkFrom,
  onOpenLinked,
}: ScaledIngredientsProps) {
  const { t } = useTranslation();
  // A scale preview knows amounts, not links: those come from the recipe, by ingredient id.
  const links = new Map(
    recipe.ingredientSections.flatMap((s) =>
      s.ingredients.flatMap((i) => (i.linkedRecipe ? [[i.id, i.linkedRecipe] as const] : [])),
    ),
  );
  const sections = preview
    ? preview.sections
    : recipe.ingredientSections.map((s) => ({
        id: s.id,
        title: s.title,
        ingredients: s.ingredients,
      }));
  const visible = sections
    .map((s) => ({
      ...s,
      ingredients: onlyIds ? s.ingredients.filter((i) => onlyIds.has(i.id)) : s.ingredients,
    }))
    .filter((s) => s.ingredients.length > 0);

  if (visible.length === 0) {
    return (
      <p className={cn('text-sm text-muted-foreground', className)}>
        {t('recipes.detail.noIngredients')}
      </p>
    );
  }

  return (
    <div
      className={cn('space-y-4', loading && 'opacity-60 transition-opacity', className)}
      aria-busy={loading || undefined}
      data-testid="ingredient-sections"
    >
      {visible.map((section) => (
        <section key={section.id} aria-label={section.title ?? t('recipes.detail.ingredients')}>
          {section.title && (
            <h3 className={cn('mb-1 font-semibold', size === 'lg' && 'text-lg')}>
              {section.title}
            </h3>
          )}
          <ul className="divide-y">
            {section.ingredients.map((ingredient) => (
              <IngredientLine
                key={ingredient.id}
                ingredient={ingredient}
                checkable={checkable}
                checked={checked?.has(ingredient.id)}
                onCheckedChange={(v) => onCheckedChange?.(ingredient.id, v)}
                size={size}
                trailing={
                  links.has(ingredient.id) ? (
                    <LinkedRecipeChip
                      link={links.get(ingredient.id)!}
                      fromRecipeId={linkFrom}
                      onOpen={onOpenLinked}
                      size={size}
                    />
                  ) : undefined
                }
              />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
